/**
 * BB-1 — step 2 of Ask for a price: what they are pricing, down to the item,
 * the sheet and the picture, with the firm's own notes and files for the trade.
 *
 * Ticks are kept by the page; this draws them and the disclosures.
 */
import { useRef, useState } from 'react';

export default function AskPricedOn({ projectId, basis, form, set, toggle, Tick, navigate,
  attachments = [], onAttach, onDropAttachment, busy }) {
  const [open, setOpen] = useState({});
  const fileRef = useRef(null);
  const flip = (key) => setOpen((was) => ({ ...was, [key]: !was[key] }));

  const itemsOf = (code) => (basis.specs.find((s) => s.code === code)?.items) || [];
  const tickedIn = (code) => itemsOf(code).filter((i) => form.item_ids.includes(i.clip_id)).length;
  const catState = (code) => {
    const n = itemsOf(code).length;
    const k = tickedIn(code);
    if (n === 0) return form.spec_codes.includes(code) ? 'true' : 'false';
    if (k === 0) return 'false';
    return k === n ? 'true' : 'mixed';
  };
  const tickCategory = (code) => {
    const ids = itemsOf(code).map((i) => i.clip_id);
    const all = catState(code) === 'true';
    const item_ids = all ? form.item_ids.filter((x) => !ids.includes(x))
      : [...new Set([...form.item_ids, ...ids])];
    const spec_codes = all ? form.spec_codes.filter((c) => c !== code)
      : [...new Set([...form.spec_codes, code])];
    set({ item_ids, spec_codes });
  };
  const tickItem = (code, clipId) => {
    const item_ids = form.item_ids.includes(clipId)
      ? form.item_ids.filter((x) => x !== clipId) : [...form.item_ids, clipId];
    const left = itemsOf(code).some((i) => item_ids.includes(i.clip_id));
    const spec_codes = left ? [...new Set([...form.spec_codes, code])]
      : form.spec_codes.filter((c) => c !== code);
    set({ item_ids, spec_codes });
  };

  const sheetsOf = (setId) => (basis.sets.find((s) => s.id === setId)?.sheets) || [];
  const setState = (setId) => {
    const sheets = sheetsOf(setId);
    const k = sheets.filter((s) => form.sheet_ids.includes(s.sheet_id)).length;
    if (!form.set_ids.includes(setId)) return 'false';
    if (sheets.length === 0 || k === 0 || k === sheets.length) return 'true';
    return 'mixed';
  };
  const tickSet = (setId) => {
    const ids = sheetsOf(setId).map((s) => s.sheet_id);
    const on = form.set_ids.includes(setId);
    set({
      set_ids: on ? form.set_ids.filter((x) => x !== setId) : [...form.set_ids, setId],
      sheet_ids: on ? form.sheet_ids.filter((x) => !ids.includes(x))
        : [...new Set([...form.sheet_ids, ...ids])],
    });
  };
  const tickSheet = (setId, sheetId) => {
    const sheet_ids = form.sheet_ids.includes(sheetId)
      ? form.sheet_ids.filter((x) => x !== sheetId) : [...form.sheet_ids, sheetId];
    const left = sheetsOf(setId).some((s) => sheet_ids.includes(s.sheet_id));
    set({
      sheet_ids,
      set_ids: left ? [...new Set([...form.set_ids, setId])]
        : form.set_ids.filter((x) => x !== setId),
    });
  };

  const photosOf = (visitId) => (basis.visits.find((v) => v.id === visitId)?.photos) || [];
  const visitState = (visitId) => {
    const photos = photosOf(visitId);
    const k = photos.filter((p) => form.photo_ids.includes(p.id)).length;
    if (!form.visit_ids.includes(visitId)) return 'false';
    if (photos.length === 0 || k === 0 || k === photos.length) return 'true';
    return 'mixed';
  };
  const tickVisit = (visitId) => {
    const ids = photosOf(visitId).map((p) => p.id);
    const on = form.visit_ids.includes(visitId);
    set({
      visit_ids: on ? form.visit_ids.filter((x) => x !== visitId) : [...form.visit_ids, visitId],
      photo_ids: on ? form.photo_ids.filter((x) => !ids.includes(x))
        : [...new Set([...form.photo_ids, ...ids])],
    });
  };
  const tickPhoto = (visitId, photoId) => {
    const photo_ids = form.photo_ids.includes(photoId)
      ? form.photo_ids.filter((x) => x !== photoId) : [...form.photo_ids, photoId];
    const left = photosOf(visitId).some((p) => photo_ids.includes(p.id));
    set({
      photo_ids,
      visit_ids: left ? [...new Set([...form.visit_ids, visitId])]
        : form.visit_ids.filter((x) => x !== visitId),
    });
  };

  const Disclose = ({ k, n, testid }) => (
    <button type="button" className="kbb-disc" onClick={(e) => { e.stopPropagation(); flip(k); }}
      data-testid={testid}>{open[k] ? '▾' : '▸'} {n}</button>
  );

  const drop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) onAttach(files);
  };

  return (
    <div onDragOver={(e) => e.preventDefault()} onDrop={drop} data-testid="ask-step2">
      <div className="kso-grp">What are they pricing? · this is exactly what they will see</div>
      <div className="kbb-col">
        <div className="kso-grp">Drawings · issued sets, current revision
          <span style={{ marginLeft: 'auto' }}>
            <button type="button" className="kso-btn" onClick={() => navigate(`/projects/${projectId}/drawings`)}>Issue a set in the Plan Room</button>
          </span>
        </div>
        {basis.sets.length === 0 ? <p className="kshp__empty">No drawing sets have been issued on this project yet. You can still send with spec sheets and photos.</p> : null}
        {basis.sets.map((s) => {
          const sheets = sheetsOf(s.id);
          const k = sheets.filter((x) => form.sheet_ids.includes(x.sheet_id)).length;
          return (
            <div key={s.id}>
              <Tick on={setState(s.id)} onClick={() => tickSet(s.id)} testid={`ask-set-${s.id}`}
                right={<Disclose k={`set-${s.id}`} n={`${sheets.length} sheets`} testid={`ask-set-open-${s.id}`} />}>
                {s.name} · {s.revision_label} · {k && k !== sheets.length ? `${k} of ${sheets.length} sheets` : `${sheets.length} sheets`}
              </Tick>
              {open[`set-${s.id}`] ? (
                <div className="kbb-sub" data-testid={`ask-set-sheets-${s.id}`}>
                  {sheets.map((sh) => (
                    <Tick key={sh.sheet_id} on={form.sheet_ids.includes(sh.sheet_id) ? 'true' : 'false'}
                      onClick={() => tickSheet(s.id, sh.sheet_id)} testid={`ask-sheet-${sh.sheet_id}`}>
                      {[sh.number, sh.title].filter(Boolean).join(' · ')}
                    </Tick>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}

        <div className="kso-grp">Spec sheets · by category</div>
        {basis.specs.map((s) => {
          const n = (s.items || []).length;
          const k = tickedIn(s.code);
          return (
            <div key={s.code}>
              <Tick on={catState(s.code)} onClick={() => tickCategory(s.code)} testid={`ask-spec-${s.code}`}
                right={n ? <Disclose k={`spec-${s.code}`} n={`${n} items`} testid={`ask-spec-open-${s.code}`} /> : <span className="m">0 items</span>}>
                {s.code} · {s.label}{k && k !== n ? <span className="src" style={{ marginLeft: 'var(--km-s2)' }}>{k} of {n}</span> : null}
              </Tick>
              {open[`spec-${s.code}`] ? (
                <div className="kbb-sub" data-testid={`ask-spec-items-${s.code}`}>
                  {(s.items || []).map((it) => (
                    <Tick key={it.clip_id} on={form.item_ids.includes(it.clip_id) ? 'true' : 'false'}
                      onClick={() => tickItem(s.code, it.clip_id)} testid={`ask-item-${it.clip_id}`}
                      right={it.spec_num ? <span className="m">{it.spec_num}</span> : null}>
                      <span className="kbb-item">
                        {it.thumbnail_url ? <img src={it.thumbnail_url} alt="" loading="lazy" /> : <i className="kbb-item__none" />}
                        <span>{it.name || it.spec_num || 'Item'}{it.room ? <span className="src" style={{ marginLeft: 'var(--km-s2)' }}>{it.room}</span> : null}</span>
                      </span>
                    </Tick>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}

        <div className="kso-grp">Photos · site visits</div>
        {basis.visits.map((v) => {
          const photos = photosOf(v.id);
          const k = photos.filter((p) => form.photo_ids.includes(p.id)).length;
          return (
            <div key={v.id}>
              <Tick on={visitState(v.id)} onClick={() => tickVisit(v.id)} testid={`ask-visit-${v.id}`}
                right={photos.length ? <Disclose k={`visit-${v.id}`} n={`${v.photo_count} photos`} testid={`ask-visit-open-${v.id}`} /> : <span className="m">{v.photo_count} photos</span>}>
                {v.label}{k && k !== photos.length ? <span className="src" style={{ marginLeft: 'var(--km-s2)' }}>{k} of {photos.length}</span> : null}
              </Tick>
              {open[`visit-${v.id}`] ? (
                <div className="kbb-photos" data-testid={`ask-visit-photos-${v.id}`}>
                  {photos.map((p) => (
                    <button type="button" key={p.id} className="kbb-photo" aria-pressed={form.photo_ids.includes(p.id)}
                      onClick={() => tickPhoto(v.id, p.id)} data-testid={`ask-photo-${p.id}`}>
                      {p.url ? <img src={p.url} alt={p.caption || ''} loading="lazy" /> : <span className="kbb-photo__none" />}
                      <span className="bx">{form.photo_ids.includes(p.id) ? '✓' : ''}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <p className="kso-hint">The set revision is locked into the request. Nothing ticked here is shared beyond this request unless you also share it on their team row.</p>

      <div className="kso-grp">Notes and attachments · for {form.trade || 'this trade'} · they see these with the request</div>
      <label className="kbb-f"><span className="k">Anything else they should know</span>
        <textarea className="kso-ta" maxLength={2000} value={form.notes}
          onChange={(e) => set({ notes: e.target.value })} data-testid="ask-notes" />
      </label>
      <div className="kso-acts" style={{ justifyContent: 'flex-start' }}>
        <button type="button" className="kso-btn" disabled={busy}
          onClick={() => fileRef.current?.click()} data-testid="ask-attach">Add files or images</button>
        <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
          onChange={(e) => { onAttach(Array.from(e.target.files || [])); e.target.value = ''; }}
          data-testid="ask-attach-input" />
        <span className="kso-hint" style={{ margin: 0 }}>Any file up to 25 MB. You can also drop them on this step.</span>
      </div>
      {attachments.map((a) => (
        <div className="kso-row" key={a.id} data-testid={`ask-attachment-${a.id}`}>
          <div className="t">{a.kind === 'image' ? <img className="kbb-att__img" src={a.url} alt="" /> : null}<b>{a.filename}</b></div>
          <button type="button" className="kso-btn" disabled={busy}
            onClick={() => onDropAttachment(a.id)} data-testid={`ask-attachment-remove-${a.id}`}>Remove</button>
        </div>
      ))}
    </div>
  );
}
